import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, ConfigProvider, Dropdown, Flex, message, Modal, Popover, Radio, Space } from 'antd'
import type { MenuProps, TabsProps } from 'antd'
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import AuthSelectors from 'modules/domain/auth/selectors'
import { generatePath, useLocation, useParams } from 'react-router-dom'
import { useHistory } from 'react-router-dom'
import { useHistoryPush } from '@agro-club/frontend-shared'
import Routes from '../route'
import SeedTrackerRoutes from '../../SeedTracker/routes'
import { AntDHeader } from 'views/ui/Header/Header'
import { useTranslation } from 'react-i18next'
import type { SeedOrder } from '../SeedOrdersList/SeedOrdersList'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { SeedOrderStatus } from 'modules/domain/seedOrders/types'
import { deleteSeedOrder, updateSeedOrderStatus } from 'modules/domain/seedOrders/managers'
import {
  headerStyle,
  PageHeader,
  PageTitle,
  SeedOrderDetailsTabs,
  seedOrderTabContainerStyles,
  seedOrderTabsBarStyles,
  seedOrderTabsStyles,
} from '../styles'
import SeedOrderDetailTab from './SeedOrderDetailTab'
import SeedOrderHistoryTab from './SeedOrderHistoryTab'

// MOCK DATA - REMOVE LATER
function getOrderFromStateOrMock(id: string | undefined, state: { seedOrder?: SeedOrder } | null): SeedOrder | null {
  if (state?.seedOrder && state.seedOrder.id === id) return state.seedOrder
  if (!id) return null
  return {
    id,
    productCardId: '—',
    soPoSivNumber: '—',
    variety: '—',
    pedigree: SeedPedigree.Certified,
    quantityMt: '—',
    originGrower: '—',
    destinationGrower: '—',
    originCompanyId: '—',
    destinationCompanyId: '—',
    lastUpdatedDate: '—',
    status: SeedOrderStatus.Pending,
    orderNumber: '—',
    purchaseOrderNumber: '—',
    sivNumber: '—',
    createdDate: '—',
    originLocation: '—',
    originRegion: '—',
    originContactName: '—',
    originContactNumber: '—',
    originContactEmail: '—',
    destinationLocation: '—',
    destinationRegion: '—',
    destinationContactName: '—',
    destinationContactNumber: '—',
    destinationContactEmail: '—',
    destinationType: undefined,
    destinationFarmerName: '—',
    destinationFarmerContactNumber: '—',
    destinationFarmerContactEmail: '—',
    destinationOtherDetails: '—',
    origin_current_mt: undefined,
    origin_requested_mt: undefined,
    origin_after_sale_mt: undefined,
  }
}

const statusOptions = Object.values(SeedOrderStatus)

const SeedOrderDetails: React.FC = () => {
  const { t } = useTranslation('seedTracker')
  const push = useHistoryPush()
  const history = useHistory()
  const [activeKey, setActiveKey] = useState('overview')
  const [order, setOrder] = useState<SeedOrder | null>(null)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [statusDraft, setStatusDraft] = useState<SeedOrderStatus>(SeedOrderStatus.Pending)
  const [statusSaving, setStatusSaving] = useState(false)
  const [modal, contextHolder] = Modal.useModal()
  const isAdmin = useSelector(AuthSelectors.isAdmin)
  const { id } = useParams<{ id: string }>()
  const location = useLocation<{
    seedOrder?: SeedOrder
    returnTo?: { route: string; params?: Record<string, string> }
  } | null>()

  // MOCK DATA - REMOVE LATER (order may be from mock when state is missing)
  const orderFromRoute = useMemo(() => getOrderFromStateOrMock(id, location.state), [id, location.state])

  useEffect(() => {
    setOrder(orderFromRoute)
    setStatusDraft(orderFromRoute?.status ?? SeedOrderStatus.Pending)
  }, [orderFromRoute])

  const goBack = useCallback(() => {
    const returnTo = location.state?.returnTo
    if (returnTo) {
      push({ route: returnTo.route, params: returnTo.params })
    } else {
      push({ route: Routes.List })
    }
  }, [push, location.state?.returnTo])

  const goToEdit = useCallback(() => {
    if (id) {
      const path = generatePath(Routes.SeedOrderEdit, { id })
      history.push(path)
    }
  }, [id, history])

  const viewInventoryForCompany = useCallback(
    (companyId: string) => {
      if (!order?.productCardId || !companyId) {
        return
      }
      const path = generatePath(SeedTrackerRoutes.SeedInventoryList, { id: order.productCardId })
      history.push(path, { selectedCompanyId: companyId })
    },
    [history, order?.productCardId],
  )

  const openStatusMenu = useCallback(() => {
    if (!order) {
      return
    }
    setStatusDraft(order.status)
    setStatusMenuOpen(true)
  }, [order])

  const closeStatusMenu = useCallback(() => {
    setStatusMenuOpen(false)
  }, [])

  const submitStatusChange = useCallback(async () => {
    if (!id || !order) {
      return
    }

    setStatusSaving(true)
    try {
      const updated = await updateSeedOrderStatus(id, statusDraft)
      setOrder((current) =>
        current
          ? {
              ...current,
              status: updated.order_status as SeedOrderStatus,
              lastUpdatedDate: updated.updated_at
                ? new Date(updated.updated_at).toLocaleDateString()
                : current.lastUpdatedDate,
            }
          : current,
      )
      setStatusMenuOpen(false)
      message.success(t('seedTracker:detail.statusChangeSuccess', 'Status updated.'))
    } catch (error) {
      console.error(error)
      message.error(t('unknown_error', { ns: 'errors' }))
    } finally {
      setStatusSaving(false)
    }
  }, [id, order, statusDraft, t])

  const handleDelete = useCallback(() => {
    if (!id || !isAdmin) {
      return
    }
    modal.confirm({
      title: t('seedTracker:detail.deleteOrderConfirmTitle'),
      content: t('seedTracker:detail.deleteConfirmContent'),
      icon: <WarningOutlined />,
      okText: t('seedTracker:detail.deleteConfirmOk'),
      okButtonProps: { danger: true },
      cancelText: t('seedTracker:detail.deleteConfirmCancel'),
      onOk: async () => {
        try {
          await deleteSeedOrder(id)
          message.success(t('seedTracker:detail.deleteOrderSuccess'))
          goBack()
        } catch (error) {
          console.error(error)
          message.error(t('unknown_error', { ns: 'errors' }))
        }
      },
    })
  }, [goBack, id, isAdmin, modal, order?.soPoSivNumber, t])

  const moreMenuItems: MenuProps['items'] = isAdmin
    ? [
        {
          key: 'delete',
          onClick: handleDelete,
          label: t('seedTracker:detail.deleteOrder'),
          icon: <DeleteOutlined />,
          danger: true,
        },
      ]
    : []

  const statusChangeMenu = (
    <Space direction="vertical" size={12} style={{ width: 220 }}>
      <Radio.Group
        value={statusDraft}
        onChange={(e) => setStatusDraft(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {statusOptions.map((status) => (
          <Radio key={status} value={status}>
            {status}
          </Radio>
        ))}
      </Radio.Group>
      <Button type="primary" block onClick={submitStatusChange} loading={statusSaving} disabled={!order}>
        {t('common:save', 'Save')}
      </Button>
    </Space>
  )

  const tabItems: TabsProps['items'] = [
    {
      key: 'overview',
      label: t('detail.tabs.overview', 'Overview'),
      children: <SeedOrderDetailTab order={order} onViewInventory={viewInventoryForCompany} />,
      destroyOnHidden: true,
      style: seedOrderTabContainerStyles,
    },
    {
      key: 'history',
      label: t('detail.tabs.history', 'History'),
      children: <SeedOrderHistoryTab />,
      destroyOnHidden: true,
      style: seedOrderTabContainerStyles,
    },
  ]

  return (
    <>
      {contextHolder}
      <AntDHeader onClickBack={goBack} styles={headerStyle} />
      <PageHeader align="center" justify="space-between">
        <PageTitle level={2}>{t('seedTracker:detail.seedOrderDetailsTitle')}</PageTitle>
        <Flex gap={12} align="center">
          <Popover
            trigger="click"
            placement="bottomRight"
            content={statusChangeMenu}
            open={statusMenuOpen}
            onOpenChange={(nextOpen) => {
              if (nextOpen) {
                openStatusMenu()
              } else {
                closeStatusMenu()
              }
            }}
          >
            <Button type="default" disabled={!order}>
              {t('seedTracker:detail.changeStatus', 'Change Status')}
            </Button>
          </Popover>
          <Button type="primary" onClick={goToEdit}>
            {t('seedTracker:detail.editOrder')}
          </Button>
          {moreMenuItems.length > 0 && (
            <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
              <Button type="default" disabled={!order}>
                {t('seedTracker:detail.more', 'More')}
              </Button>
            </Dropdown>
          )}
        </Flex>
      </PageHeader>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              horizontalMargin: '0',
            },
          },
        }}
      >
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <SeedOrderDetailsTabs
            style={seedOrderTabsStyles}
            tabBarStyle={seedOrderTabsBarStyles}
            items={tabItems}
            activeKey={activeKey}
            onChange={setActiveKey}
          />
        </div>
      </ConfigProvider>
    </>
  )
}

export default SeedOrderDetails
